import { NumericValueOfNotAvailable, elementStatus } from '../utils/constants';
import { getCurrentUpsell, getCurrentUpsellType, getParameters, isInternalBrand } from '../main/selectors';

import { PARAMETER_REQUIREDUPSELLSONLY } from '../main/constants';
import { UpsellType } from './constants';
import { createSelector } from 'reselect';
import { getBIAvailability } from '../text/logics';
import { getHotStampColorImageUrl } from '../utils/scene7url';
import { getHotStampedCheck } from '../imprintarea/logics';
import { getQuantity } from '../bom/selectors';
import { isValidQuantity } from '../utils/quantityValidation';

export const getUpsells = createSelector(
  getCurrentUpsellType,
  (state) => state.upsells,
  (upsellType, upsells) => {
    return upsellType === UpsellType.required
      ? upsells.filter((upsell) => upsell && upsell.item && upsell.item.isRequired)
      : upsellType === UpsellType.optional
      ? upsells.filter((upsell) => upsell && upsell.item && !upsell.item.isRequired)
      : upsells;
  }
);

const getMainItemQuantity = createSelector(
  getQuantity,
  (state) => state.parameters.mainItemQuantity,
  (mainItemQuantity, mainItemQuantityForCartUpsells) => {
    return mainItemQuantityForCartUpsells ? mainItemQuantityForCartUpsells : mainItemQuantity;
  }
);

const getMinimumQuantityHelper = (price) => {
  return price && price.priceTable && price.priceTable.length > 0
    ? Math.min(...price.priceTable.map((row) => row.quantity))
    : NumericValueOfNotAvailable;
};

const isFreeUpsell = (upsell) => {
  const { price } = upsell;
  return price && price.priceTable && price.priceTable.length === 1 && price.priceTable[0].unitPrice === 0;
};

export const getUpsellsValidation = createSelector(
  getMainItemQuantity,
  isInternalBrand,
  (state) => state.upsells,
  (mainItemQuantity, internalBrand, upsells) => {
    let validation = { invalidItemId: '', isValid: true, message: '' };
    for (const upsell of upsells) {
      if (!upsell.isSelected) continue;

      const { item, price, totalQuantity } = upsell;
      const miniQty = getMinimumQuantityHelper(price);

      if (totalQuantity <= 0) {
        validation = {
          invalidItemId: upsell.item.itemId,
          isValid: false,
          message: 'Please enter the quantity for the selected item(s) to continue.',
        };
        break;
      } else if (totalQuantity < miniQty) {
        validation = {
          invalidItemId: upsell.item.itemId,
          isValid: false,
          message: `Your order must have a minimum total quantity of ${miniQty} to continue. Please enter a valid quantity.`,
        };
        break;
      } else {
        const result = isValidQuantity(
          totalQuantity,
          item.fullPackageQuantity,
          miniQty,
          price.priceTable,
          internalBrand
        );
        if (!result.isValid) {
          validation = {
            invalidItemId: upsell.item.itemId,
            isValid: false,
            message: result.message,
          };
          break;
        }
      }
    }
    if (!validation.isValid) return validation;

    let freeUpsellsTotalQuantity = 0;
    let firstFreeUpsellItemId = undefined;
    const message = `You have selected more than the allowed quantity of ${mainItemQuantity} free items. Please update your selection.`;
    for (const upsell of upsells) {
      if (!upsell.isSelected || !isFreeUpsell(upsell)) continue;

      if (firstFreeUpsellItemId === undefined) {
        firstFreeUpsellItemId = upsell.item.itemId;
      }

      const { totalQuantity } = upsell;
      if (totalQuantity > mainItemQuantity) {
        validation = {
          invalidItemId: upsell.item.itemId,
          isValid: false,
          message,
        };
        break;
      }

      freeUpsellsTotalQuantity += totalQuantity;
      if (freeUpsellsTotalQuantity > mainItemQuantity) {
        validation = {
          invalidItemId: firstFreeUpsellItemId,
          isValid: false,
          message,
        };
        break;
      }
    }
    return validation;
  }
);

export const getUpsellItem = createSelector(
  (state) => state.upsells,
  (_, itemId) => itemId,
  (upsells, itemId) => {
    return upsells.find((upsell) => upsell && upsell.item && upsell.item.itemId && upsell.item.itemId === itemId);
  }
);

export const getPriceForUpsellItem = createSelector(getUpsellItem, (upsell) => {
  return upsell && upsell.price ? upsell.price : undefined;
});

export const getMinimumQuantityForUpsellItem = createSelector(getPriceForUpsellItem, (price) => {
  return getMinimumQuantityHelper(price);
});

export const getSelectedCustomizableUpsellItemIds = createSelector(getUpsells, (upsells) => {
  return upsells
    .filter((upsell) => upsell.isSelected && upsell.totalQuantity > 0 && upsell.item.isCustomizable)
    .map((upsell) => upsell.item.itemId);
});

export const getCurrentUpsellItemIntroduction = createSelector(getCurrentUpsell, (upsell) => {
  let itemName = '';
  let itemId = '';
  let matchedOptionText = '';
  if (upsell) {
    itemName = upsell.item.name;
    itemId = upsell.item.itemId;
    const { matchedQuestionCodeForUpsell, uniqueOptions } = upsell;
    if (matchedQuestionCodeForUpsell && Object.keys(uniqueOptions).includes(matchedQuestionCodeForUpsell)) {
      const selectedOption = uniqueOptions[matchedQuestionCodeForUpsell].find((option) => option.isSelected);
      matchedOptionText = selectedOption ? selectedOption.description : '';
    }
  }
  return { itemName, itemId, matchedOptionText };
});

export const getCurrentUpsellItemPreviewUrl = createSelector(getCurrentUpsell, (upsell) => {
  return upsell && upsell.fullPreviewUrl ? upsell.fullPreviewUrl : upsell && upsell.previewUrl ? upsell.previewUrl : '';
});

export const getCurrentUpsellItemBomSummary = createSelector(getCurrentUpsell, (upsell) => {
  if (upsell && upsell.hasBomOptions) {
    const { matchedQuestionCodeForUpsell, questions, uniqueOptions } = upsell;
    return questions
      .filter((question) => question.code !== matchedQuestionCodeForUpsell)
      .map((question) => ({
        question,
        option: uniqueOptions[question.code].find((option) => option.isSelected),
      }));
  } else {
    return [];
  }
});

export const getRequiredUpsellsOnlyFlag = createSelector(getParameters, (parameters) => {
  return Object.keys(parameters).includes(PARAMETER_REQUIREDUPSELLSONLY)
    ? parameters[PARAMETER_REQUIREDUPSELLSONLY]
    : false;
});

export const getWhetherHasOptionalUpsellsOrNot = createSelector(
  (state) => state.upsells,
  (upsells) => upsells.some((upsell) => upsell && upsell.item && !upsell.item.isRequired)
);

export const getCurrentUpsellImprintColor = createSelector(getCurrentUpsell, (upsell) => {
  let upsellImprintColor = null;
  if (upsell) {
    if (upsell.imprintAreas.length > 0) {
      const upsellFormat = upsell.imprintAreas[0];
      const isHotStamped = getHotStampedCheck(upsellFormat.processType);
      if (upsellFormat && upsell.textDict) {
        const textList = upsell.textDict[upsellFormat.key];
        if (textList && textList.length > 0) {
          const textColor = textList[0].data.color;
          if (textColor) {
            upsellImprintColor = {
              colorReference: isHotStamped ? '' : `#${textColor.colorReference}`,
              description: textColor.colorDescription,
              imageUrl: isHotStamped ? getHotStampColorImageUrl(textColor.colorId) : '',
            };
          }
        }
      }
    }
  }
  return upsellImprintColor;
});

// upsells only have one imprint area
export const getCurrentUpsellImprintArea = createSelector(getCurrentUpsell, (upsell) => {
  if (upsell && upsell.imprintAreas.length > 0) {
    return upsell.imprintAreas[0] ? upsell.imprintAreas[0] : null;
  }
});

export const getCurrentUpsellImprintAreaKey = createSelector(getCurrentUpsellImprintArea, (imprintArea) => {
  return imprintArea && imprintArea.key ? imprintArea.key : null;
});

export const getCurrentUpsellImprintAreaCode = createSelector(getCurrentUpsellImprintArea, (imprintArea) => {
  return imprintArea && imprintArea.imprintAreaCode ? imprintArea.imprintAreaCode : null;
});

export const getCurrentUpsellTextList = createSelector(
  getCurrentUpsell,
  getCurrentUpsellImprintAreaKey,
  (upsell, imprintAreaKey) => {
    let upselltextList = [];
    if (
      upsell.textDict &&
      upsell.textDict[imprintAreaKey] !== undefined &&
      upsell.textDict[imprintAreaKey].length > 0
    ) {
      upsell.textDict[imprintAreaKey].forEach((text) => {
        let textData = Object.assign({}, text.data);
        textData.studioItemType = upsell.item.studioItemType;
        upselltextList.push({ data: textData, ui: text.ui });
      });
    }
    return upselltextList;
  }
);

export const getSelectedUpsellsTextDataDict = (itemId) => {
  return createSelector(
    (state) => state.upsells,
    (upsells) => {
      let textDataDict = {};
      let upsell = upsells.find(
        (upsell) => upsell && upsell.item && upsell.item.itemId && upsell.item.itemId === itemId
      );
      if (upsell.textDict !== null) {
        for (let key of Object.keys(upsell.textDict)) {
          let textDataList = [];
          if (upsell.textDict[key] !== undefined) {
            upsell.textDict[key].forEach((text) => {
              let textData = Object.assign({}, text.data);
              textDataList.push(textData);
            });
            textDataDict[key] = textDataList;
          }
        }
      }
      return textDataDict;
    }
  );
};

export const getCurrentUpsellTextDataDict = createSelector(
  getCurrentUpsell,
  getCurrentUpsellImprintAreaKey,
  (upsell, imprintAreaKey) => {
    let textDataDict = {};
    let textDataList = [];
    if (
      upsell &&
      upsell.textDict &&
      upsell.textDict[imprintAreaKey] !== undefined &&
      upsell.textDict[imprintAreaKey].length > 0
    ) {
      upsell.textDict[imprintAreaKey].forEach((text) => {
        let textData = Object.assign({}, text.data);
        textData.studioItemType = upsell.item.studioItemType;
        textDataList.push(textData);
      });
      textDataDict[imprintAreaKey] = textDataList;
    }
    return textDataDict;
  }
);

export const getCurrentUpsellTextDataList = createSelector(
  getCurrentUpsellTextDataDict,
  getCurrentUpsellImprintAreaKey,
  (upsellTextDataDict, imprintAreaKey) => {
    const nonDigitalTextDataList = upsellTextDataDict[imprintAreaKey];
    return nonDigitalTextDataList;
  }
);

export const getCurrentUpsellImageDataDict = createSelector(
  getCurrentUpsell,
  getCurrentUpsellImprintAreaKey,
  (upsell, imprintAreaKey) => {
    let imageDataDict = {};
    if (upsell.imageDict[imprintAreaKey] !== undefined && upsell.imageDict[imprintAreaKey].length > 0) {
      imageDataDict[imprintAreaKey] = upsell.imageDict[imprintAreaKey].map((image) => {
        const { studioItemType } = upsell.item;
        return { ...image, studioItemType };
      });
    }
    return imageDataDict;
  }
);

export const getUpsellSpecialSymbols = createSelector(getCurrentUpsell, (upsell) => {
  return upsell.specialSymbols;
});

export const getCurrentUpsellElement = createSelector(getCurrentUpsellTextList, (upsellTextList) => {
  let currentElement = null;
  if (upsellTextList) {
    upsellTextList.forEach((element) => {
      if (element.ui.status === elementStatus.SingleSelected) {
        currentElement = element;
        return false;
      }
    });
  }
  return currentElement;
});

export const getCurrentUpsellFontList = createSelector(
  getCurrentUpsellImprintAreaCode,
  getCurrentUpsell,
  (imprintAreaCode, upsell) => {
    const fontList = upsell.fontDict[imprintAreaCode];
    return fontList;
  }
);

export const getDisplayFontListFromFontList = function (upsellFontList) {
  const displayFontList = [];
  upsellFontList.forEach((item1) => {
    if (!displayFontList.some((item2) => item2.fontFace === item1.fontFace)) {
      displayFontList.push({
        fontFace: item1.fontFace,
        fontImgFileName: item1.fontImgFileName,
        fontFileName: item1.fontFileName,
        selected: false,
      });
    }
  });
  return displayFontList;
};

export const getUpsellDisplayFontList = createSelector(
  getCurrentUpsellFontList,
  getCurrentUpsellElement,
  (upsellFontList, selectedTextElement) => {
    let displayFontList = [];
    if (selectedTextElement) {
      const fontFace = selectedTextElement.data.fontFace;
      displayFontList = getDisplayFontListFromFontList(upsellFontList);
      displayFontList.forEach((item3) => {
        if (item3.fontFace === fontFace) {
          item3.selected = true;
          return false;
        }
        return true;
      });
    }
    return displayFontList;
  }
);

export const getTextStyleStatus = createSelector(
  getCurrentUpsell,
  getCurrentUpsellElement,
  getCurrentUpsellFontList,
  getCurrentUpsellTextList,
  (upsell, currentElement, fontList, upsellTextList) => {
    if (upsellTextList) {
      const selectedTextElement = upsellTextList.find((element) => element.ui.status === elementStatus.SingleSelected);
      if (selectedTextElement) {
        const isBold = selectedTextElement.data.isBold;
        const isItalic = selectedTextElement.data.isItalic;
        const currentStudioItemType = upsell.item.studioItemType;
        const biAvailability = getBIAvailability(currentElement, fontList);
        return { currentStudioItemType, isBold, isItalic, ...biAvailability };
      }
    }
    return {};
  }
);

export const getUpsellHeightFit = createSelector(
  getCurrentUpsellImprintAreaKey,
  getCurrentUpsell,
  (imprintAreaKey, upsell) => {
    // When heightFits equals null, that is, the initialization phase, assign it true
    return upsell.heightFits[imprintAreaKey] ? upsell.heightFits[imprintAreaKey] : true;
  }
);

export const getInternetDescriptionForRequiredUpsells = createSelector(
  getCurrentUpsellType,
  getUpsells,
  (upsellType, upsells) => {
    let desc = '';
    if (upsellType === UpsellType.required) {
      const targetUpsell = upsells.find((upsell) => upsell.item.internetDesc);
      if (targetUpsell) desc = targetUpsell.item.internetDesc.toLowerCase();
    }
    return desc;
  }
);

export const getUpsellImprintTextList = createSelector(getCurrentUpsellTextDataList, (upsellTextDataList) => {
  let imprintTextList =
    upsellTextDataList &&
    upsellTextDataList.filter(
      (text) => text.imprintText && text.imprintText.toUpperCase() !== text.defaultText.trim().toUpperCase()
    );
  return imprintTextList;
});

export const getIfUserNeedImprintText = createSelector(
  (state) => state.ui,
  (ui) => ui.askIfUserNeedImprintText
);
